// frontend/user/src/components/file-view/use-file-meta.ts
// 文件详情/预览页共享的元数据读取 hook(从 routes/file-preview.tsx 抽出,逻辑不变)。
//   file_info(+ base_file 平坦化)+ image_info / audio_info / video_info(按 base_file_id)
//   + 封面 URL 解析。RLS 经 file_info owner / *_select_via_file。无 *_info 行时字段降级 "—" 不崩。
// 详情页(/file)与预览页(/preview)共用此 hook 的只读部分;owner 专属写操作(改封面/标签)留在页面层。

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getSupabase } from '@dogdisk/shared'
import { resourceUrl } from '../../_lib/resource-node-pref'

export type FileMeta = {
  id: string
  name: string
  size: number | null
  mime_type: string | null
  created_at: string
  base_file_id: string | null
  cover_file_info_id: string | null
  parent_id: string | null
}

export type ImageInfo = { width: number | null; height: number | null }
export type AudioInfo = {
  duration_ms: number | null
  bitrate_bps: number | null
  codec: string | null
  cover_file_info_id: string | null
}
export type VideoInfo = {
  duration_ms: number | null
  width: number | null
  height: number | null
  video_codec: string | null
}

/** ms → mm:ss 或 h:mm:ss */
export function fmtDuration(ms: number | null | undefined): string {
  if (ms == null || ms <= 0) return '—'
  const total = Math.round(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

/** bps → "256 kbps" */
export function fmtBitrate(bps: number | null | undefined): string {
  if (bps == null || bps <= 0) return '—'
  return `${Math.round(bps / 1000)} kbps`
}

export function fmtResolution(w: number | null | undefined, h: number | null | undefined): string {
  if (!w || !h) return '—'
  return `${w}×${h}`
}

export function streamUrlOf(baseFileId: string | null, name: string | null): string | null {
  if (!baseFileId || !name) return null
  return resourceUrl(`/gapi/file/${baseFileId}/${encodeURIComponent(name)}`)
}

/**
 * 读取单个文件的元数据 + 媒体信息 + 封面 URL,并派生 mime 判定。
 * 返回的 query 对象保持 react-query 语义(isLoading/data/...),供页面分支渲染。
 */
export function useFileMeta(fileId: string | undefined) {
  const meta = useQuery({
    queryKey: ['file', 'meta', fileId] as const,
    enabled: Boolean(fileId),
    queryFn: async () => {
      const sb = getSupabase()
      // size + mime_type 在 base_file;嵌套 select 后平坦化保持 FileMeta 契约。
      // 2026-06-02 file_info 锁死:tags / is_public 列已删 / 不可编辑,不再 select。
      // eslint-disable-next-line no-restricted-syntax -- 单文件 by-id 读(file_info/媒体元信息;合法跨用户公开/分享访问,非列表越权面)
      const { data, error } = await sb
        .from('file_info')
        .select(
          'id, name, created_at, parent_id, base_file_id, cover_file_info_id, base_file:base_file_id(size_original, mime_type)',
        )
        .eq('id', fileId!)
        .maybeSingle()
      if (error) throw error
      if (!data) return null
      const row = data as unknown as {
        id: string
        name: string
        created_at: string
        parent_id: string | null
        base_file_id: string | null
        cover_file_info_id: string | null
        base_file: { size_original: number; mime_type: string } | null
      }
      return {
        id: row.id,
        name: row.name,
        created_at: row.created_at,
        parent_id: row.parent_id,
        size: row.base_file?.size_original ?? null,
        mime_type: row.base_file?.mime_type ?? null,
        base_file_id: row.base_file_id,
        cover_file_info_id: row.cover_file_info_id,
      } satisfies FileMeta
    },
  })

  const mime = meta.data?.mime_type ?? ''
  const isImage = mime.startsWith('image/')
  const isVideo = mime.startsWith('video/')
  const isAudio = mime.startsWith('audio/')
  const isMedia = isImage || isVideo || isAudio
  const baseFileId = meta.data?.base_file_id ?? null

  const imageInfo = useQuery({
    queryKey: ['file', 'image_info', baseFileId] as const,
    enabled: isImage && Boolean(baseFileId),
    queryFn: async (): Promise<ImageInfo | null> => {
      const sb = getSupabase()
      // eslint-disable-next-line no-restricted-syntax -- 单文件 by-id 读(file_info/媒体元信息;合法跨用户公开/分享访问,非列表越权面)
      const { data, error } = await sb
        .from('image_info')
        .select('width, height')
        .eq('base_file_id', baseFileId!)
        .maybeSingle()
      if (error) throw error
      return (data as ImageInfo | null) ?? null
    },
  })

  const audioInfo = useQuery({
    queryKey: ['file', 'audio_info', baseFileId] as const,
    enabled: isAudio && Boolean(baseFileId),
    queryFn: async (): Promise<AudioInfo | null> => {
      const sb = getSupabase()
      // eslint-disable-next-line no-restricted-syntax -- 单文件 by-id 读(file_info/媒体元信息;合法跨用户公开/分享访问,非列表越权面)
      const { data, error } = await sb
        .from('audio_info')
        .select('duration_ms, bitrate_bps, codec, cover_file_info_id')
        .eq('base_file_id', baseFileId!)
        .maybeSingle()
      if (error) throw error
      return (data as AudioInfo | null) ?? null
    },
  })

  const videoInfo = useQuery({
    queryKey: ['file', 'video_info', baseFileId] as const,
    enabled: isVideo && Boolean(baseFileId),
    queryFn: async (): Promise<VideoInfo | null> => {
      const sb = getSupabase()
      // eslint-disable-next-line no-restricted-syntax -- 单文件 by-id 读(file_info/媒体元信息;合法跨用户公开/分享访问,非列表越权面)
      const { data, error } = await sb
        .from('video_info')
        .select('duration_ms, width, height, video_codec')
        .eq('base_file_id', baseFileId!)
        .maybeSingle()
      if (error) throw error
      return (data as VideoInfo | null) ?? null
    },
  })

  // 封面解析:audio 用 audio_info.cover_file_info_id;video/通用 用 file_info.cover_file_info_id
  const coverId = isAudio
    ? (audioInfo.data?.cover_file_info_id ?? meta.data?.cover_file_info_id ?? null)
    : (meta.data?.cover_file_info_id ?? null)

  const coverUrl = useQuery({
    queryKey: ['file', 'cover-url', coverId] as const,
    enabled: Boolean(coverId),
    queryFn: async (): Promise<string | null> => {
      const sb = getSupabase()
      // eslint-disable-next-line no-restricted-syntax -- 单文件 by-id 读(file_info/媒体元信息;合法跨用户公开/分享访问,非列表越权面)
      const { data, error } = await sb
        .from('file_info')
        .select('name, base_file_id')
        .eq('id', coverId!)
        .maybeSingle()
      if (error) throw error
      const row = data as { name: string; base_file_id: string | null } | null
      return row ? streamUrlOf(row.base_file_id, row.name) : null
    },
  })

  const streamURL = useMemo(
    () => streamUrlOf(meta.data?.base_file_id ?? null, meta.data?.name ?? null),
    [meta.data],
  )

  return {
    meta,
    imageInfo,
    audioInfo,
    videoInfo,
    coverUrl,
    mime,
    isImage,
    isVideo,
    isAudio,
    isMedia,
    baseFileId,
    streamURL,
  }
}
